/**
 * Status of an edit suggestion surfaced from a modify_prompt tool call.
 * Projected from the persisted `ToolResult`:
 *   rejected            → "rejected"
 *   accepted + isError  → "failed"
 *   accepted + ok       → "accepted"
 *   no result           → "pending"
 */
export type EditSuggestionStatus = "pending" | "accepted" | "rejected" | "failed";

/** Edit suggestion from a modify_prompt tool call. */
export interface EditSuggestion {
  id: string;
  toolCallId: string;
  // snake_case: mirrors LLM tool argument names from the modify_prompt tool definition
  old_string: string;
  new_string: string;
  replace_all: boolean;
  explanation?: string;
  status: EditSuggestionStatus;
  errorMessage?: string;
}

/**
 * Canonical result messages written by CuePromptEditApplier (the single executor
 * for modify_prompt in both auto and manual modes). Both paths import these so
 * the persisted TOOL row text is identical regardless of mode, and the public-API
 * mapper can resolve each outcome to the correct suggestion status.
 *
 * DECLINED and CANCELLED are the two `reason` sentences a rejection carries — the
 * single human-readable string the model reads on the next turn and the user sees
 * in the UI. They are PRODUCED from `status: "rejected"`, never parsed back.
 */
export const CUE_APPLY_RESULT_MESSAGES = {
  /** The applier found old_string and wrote the updated promptJson. */
  APPLIED: "The edit was applied successfully.",
  /** The applier found no match for old_string — no mutation occurred. */
  NOT_FOUND: "The edit was not applied — old_string not found in the prompt.",
  /** The user saw the suggestion and explicitly declined it. `reason` on a rejection. */
  DECLINED: "The user declined the edit.",
  /** The generation was stopped before the tool call was resolved. `reason` on a rejection. */
  CANCELLED: "The generation was cancelled."
} as const;

/**
 * Per-tool skip reason sentences — the string the model reads when the user
 * sends a new message while a suggestion is still pending. These are the single
 * source of truth; both orchestrators import `skippedReasonForTool` so the
 * persisted TOOL row text is identical regardless of the code path.
 *
 * Skip is NOT a new status — it is a `rejected` TOOL row with a skip-flavored
 * reason, exactly as DECLINED is a rejected row with the decline reason.
 */
export const CUE_SKIP_RESULT_MESSAGES = {
  ask_user_question: "The user skipped answering this question and sent a new message instead.",
  modify_prompt: "The user skipped this edit suggestion and sent a new message instead.",
  default: "The user skipped responding and sent a new message instead."
} as const;

export function skippedReasonForTool(toolName: string): string {
  if (toolName === "ask_user_question") return CUE_SKIP_RESULT_MESSAGES.ask_user_question;
  if (toolName === "modify_prompt") return CUE_SKIP_RESULT_MESSAGES.modify_prompt;
  return CUE_SKIP_RESULT_MESSAGES.default;
}

export const CUE_TOOL_RESULTS = {
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  FAILED: "failed"
} as const;

export const CUE_LLM_ERRORS = {
  NO_MATCH_PROMPT_CHANGED:
    "error: No match found — the prompt has been modified since you last read it. Your old_string no longer exists in the current prompt. Re-read the updated prompt provided below and retry with the correct text.",
  NO_MATCH_WHITESPACE:
    "error: No match found — old_string does not exist in the prompt. Check for whitespace issues.",
  MULTIPLE_MATCHES: (matchCount: number) =>
    `error: Multiple matches (${matchCount} found) — old_string is not unique. Include more surrounding context.` as const,
  FALLBACK: (detail?: string) => `error: ${detail ?? "Failed to apply edit"}` as const
} as const;

export const CUE_SUGGESTION_LABELS = {
  ACCEPTED: "Accepted",
  REJECTED: "Rejected",
  FAILED: "Edit failed"
} as const;

export const CUE_SUGGESTION_ERROR_MESSAGES = {
  NO_MATCH_IN_PROMPT: "No match found in the prompt",
  FALLBACK: "Failed to apply edit"
} as const;

export const CUE_LLM_CONTEXT = {
  EDIT_FAILED_VALIDATION: "The edit failed validation. Here is the current prompt (unchanged):",
  EDIT_APPLIED: "The edit was applied. Here is the updated prompt:",
  EDIT_DECLINED: "The user declined the edit. Here is the current prompt (unchanged):"
} as const;

export const CUE_VALIDATION_ERROR_LABELS = {
  DUPLICATE_TAB: "Duplicate tab detected",
  PROTECTED_SECTION_MODIFIED: "Protected section cannot be edited",
  PROTECTED_SECTION_DELETED: "Protected section cannot be removed",
  PROTECTED_ATTRIBUTE_MODIFIED: "Section attribute cannot be changed",
  INVALID_FORMAT: "Edit produced invalid content",
  INVALID_STRUCTURE: "Edit produced invalid structure",
  MISSING_REQUIRED_TAB: "Missing required deployment tab",
  MULTIPLE_MATCH: "Multiple matches found",
  PROMPT_CHANGED: "Prompt changed — no match found"
} as const;

export const CUE_UNAVAILABLE_MESSAGES = {
  HISTORICAL_TITLE: "Cue is not available on historical versions",
  HISTORICAL_DESCRIPTION: "Switch back to the current draft to use Cue.",
  TAB_TITLE: "Cue is not available on this tab yet",
  TAB_DESCRIPTION: "Switch to the Prompt tab to use Cue."
} as const;

export const CUE_BE_TOOL_ERRORS = {
  UNSUPPORTED_TYPE: (type: string) => `Unsupported tool call type: ${type}` as const,
  INVALID_TOOL_CALL: (name: string, argsStatus: string) =>
    `Invalid tool call (name: ${name}, args: ${argsStatus})` as const,
  UNKNOWN_TOOL: (name: string) => `Unknown tool: ${name}` as const,
  UNKNOWN_READ_TOOL: (name: string) => `Unknown read tool: ${name}` as const,
  READ_TOOL_FAILED: (name: string) => `Failed to resolve ${name}` as const
} as const;
